import { useRepairShopContext } from "@/context/RepairShopContext";

interface DailySummaryReportProps {
  sales: any[];
  repairs?: any[];
  dateRange: { from: Date; to: Date };
}

export const generateDailySummaryHTML = ({
  sales,
  repairs = [],
  dateRange,
}: DailySummaryReportProps) => {
  const activeSales = sales.filter(sale => sale.status !== "returned");
  const activeRepairs = repairs.filter(r => r.status !== "returned" && r.status !== "cancelled");
  
  const salesRevenue = activeSales.reduce((sum, sale) => sum + sale.totalAmount, 0);
  const repairsRevenue = activeRepairs.reduce((sum, r) => sum + (Number(r.repairPrice) || 0), 0);
  const totalRevenue = salesRevenue + repairsRevenue;

  const totalTransactions = activeSales.length + activeRepairs.length;
  const averageTransaction =
    totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
  const totalItems = activeSales.reduce(
    (sum, sale) => sum + (sale.items?.length || 0),
    0
  );

  // Calculate Total Cost and Raw Income
  const totalCost = activeSales.reduce((sum, sale) => {
    const saleCost =
      sale.items?.reduce((itemSum: number, item: any) => {
        const itemCost = Number(item.product?.cost) || 0;
        return itemSum + itemCost * item.quantity;
      }, 0) || 0;
    return sum + saleCost;
  }, 0);

  const rawIncome = totalRevenue - totalCost;

  // Payment method breakdown
  const paymentMethods = activeSales.reduce((acc, sale) => {
    acc[sale.paymentMethod] = (acc[sale.paymentMethod] || 0) + sale.totalAmount;
    return acc;
  }, {});

  // Hourly breakdown
  const hourlyData = activeSales.reduce((acc, sale) => {
    const hour = new Date(sale.createdAt).getHours();
    acc[hour] = (acc[hour] || 0) + sale.totalAmount;
    return acc;
  }, {});

  const peakHour = Object.entries(hourlyData).reduce<{
    hour?: number;
    amount?: number;
  }>(
    (peak, [hour, amount]) =>
      (amount as number) > (peak.amount || 0)
        ? { hour: parseInt(hour), amount: amount as number }
        : peak,
    {}
  );

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Rapport d'activité journalière</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #000; font-weight: bold; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #000; padding-bottom: 20px; }
        .shop-name { font-size: 24px; font-weight: 900; margin-bottom: 5px; }
        .report-title { font-size: 18px; color: #000; margin-bottom: 10px; font-weight: bold; }
        .date-range { font-size: 14px; color: #000; font-weight: bold; }
        .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; }
        .metric-card { text-align: center; padding: 20px; border: 2px solid #000; border-radius: 8px; }
        .metric-value { font-size: 28px; font-weight: 900; color: #000; margin-bottom: 5px; }
        .metric-label { font-size: 14px; color: #000; font-weight: bold; }
        .section { margin: 30px 0; }
        .section-title { font-size: 16px; font-weight: 900; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 5px; }
        .payment-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
        .payment-item { padding: 15px; background: #f0f0f0; border: 1px solid #000; border-radius: 6px; text-align: center; }
        .payment-method { font-weight: 900; margin-bottom: 5px; text-transform: capitalize; }
        .payment-amount { font-size: 18px; color: #000; font-weight: bold; }
        .peak-hour { background: #f0f0f0; border: 1px solid #000; padding: 15px; border-radius: 6px; text-align: center; }
        .sales-table { width: 100%; border-collapse: collapse; margin-top: 15px; border: 2px solid #000; }
        .sales-table th, .sales-table td { padding: 10px; text-align: left; border-bottom: 1px solid #000; font-weight: bold; }
        .sales-table th { background: #f0f0f0; font-weight: 900; }
        .amount { color: #000; font-weight: 900; }
        .footer-stats { margin-top: 30px; text-align: right; border-top: 1px solid #000; padding-top: 10px; font-size: 12px; }
        .status-returned { color: #dc2626; text-decoration: line-through; }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="shop-name">MobyPro</div>
        <div class="report-title">Rapport d'activité journalière</div>
        <div class="date-range">${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}</div>
      </div>

      <div class="metrics">
        <div class="metric-card">
          <div class="metric-value">${totalRevenue.toFixed(3)}</div>
          <div class="metric-label">Revenu Total (TND)</div>
          <div style="font-size: 10px; color: #666; margin-top: 5px;">
            Ventes : ${salesRevenue.toFixed(3)} | Réparations : ${repairsRevenue.toFixed(3)}
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-value">${rawIncome.toFixed(3)}</div>
          <div class="metric-label">Revenu Net</div>
        </div>
        <div class="metric-card">
          <div class="metric-value">${totalItems}</div>
          <div class="metric-label">Articles Vendus</div>
          <div style="font-size: 10px; color: #666; margin-top: 5px;">
            Ventes : ${activeSales.length} | Réparations : ${activeRepairs.length}
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Méthodes de Paiement</div>
        <div class="payment-grid">
          ${Object.entries(paymentMethods)
            .map(
              ([method, amount]) => `
            <div class="payment-item">
              <div class="payment-method">${method}</div>
              <div class="payment-amount">${(amount as number).toFixed(
                3
              )} TND</div>
            </div>
          `
            )
            .join("")}
        </div>
      </div>

      ${
        peakHour.hour !== undefined
          ? `
        <div class="section">
          <div class="section-title">Heure de Pointe</div>
          <div class="peak-hour">
            <strong>${peakHour.hour}:00 - ${peakHour.hour + 1}:00</strong>
            <span style="margin-left: 20px;">${(
              peakHour.amount as number
            ).toFixed(3)} TND</span>
          </div>
        </div>
      `
          : ""
      }

      <div class="section">
        <div class="section-title">Détails des Ventes</div>
        <table class="sales-table">
          <thead>
            <tr>
              <th>N° Vente</th>
              <th>Articles (Produits)</th>
              <th>Paiement</th>
              <th>Coût</th>
              <th>Prix</th>
              <th>Statut</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${sales
              .map((sale) => {
                const isReturned = sale.status === "returned";
                const saleCost =
                  sale.items?.reduce((itemSum: number, item: any) => {
                    const itemCost = Number(item.product?.cost) || 0;
                    return itemSum + itemCost * item.quantity;
                  }, 0) || 0;

                const productNames = sale.items?.map((item: any) => {
                  let name = `${item.quantity}x ${item.product?.name}`;
                  const lowerName = item.product?.name?.toLowerCase() || "";
                  if (lowerName.includes("reparat") || lowerName.includes("repair")) {
                    const matchingRepair = repairs?.find(
                      (r) => 
                        r.customerName === sale.customerName ||
                        (Math.abs(Number(r.repairPrice) - item.totalPrice) < 0.1 && new Date(r.createdAt).toDateString() === new Date(sale.createdAt).toDateString())
                    );
                    if (matchingRepair && matchingRepair.ticketNumber) {
                      name += ` (Ticket #${matchingRepair.ticketNumber})`;
                    }
                  }
                  return name;
                }).join(", ") || "-";
                const detailedItems = sale.notes ? `${productNames}<br/><span style="font-size: 0.8em; color: #666; font-style: italic;">Note : ${sale.notes}</span>` : productNames;
                const statusStyle = isReturned ? 'class="status-returned"' : '';
                const displayStatus = isReturned ? 'RETOURNÉ' : (sale.status === 'completed' ? 'terminé' : sale.status || 'terminé');

                return `
              <tr ${isReturned ? 'style="color: #666; background-color: #f9f9f9;"' : ''}>
                <td>${sale.saleNumber}</td>
                <td>${detailedItems}</td>
                <td style="text-transform: capitalize;">${
                  sale.paymentMethod
                }</td>
                <td>${saleCost.toFixed(3)} TND</td>
                <td class="amount" ${statusStyle}>${sale.totalAmount.toFixed(3)} TND</td>
                <td style="text-transform: uppercase; font-size: 0.8em; ${isReturned ? 'color: #dc2626;' : ''}">${displayStatus}</td>
                <td>${new Date(sale.createdAt).toLocaleString()}</td>
              </tr>
            `;
              })
              .join("")}
          </tbody>
        </table>
      </div>

      ${repairs && repairs.length > 0 ? `
      <div class="section">
        <div class="section-title">Détails des Réparations</div>
        <table class="sales-table">
          <thead>
            <tr>
              <th>N° Ticket</th>
              <th>Client</th>
              <th>Appareil</th>
              <th>Problème</th>
              <th>Prix</th>
              <th>Paiement</th>
              <th>Statut</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${repairs
              .map((repair) => {
                const isCancelled = repair.status === "cancelled";
                const isReturned = repair.status === "returned";
                const fadeClasses = (isCancelled || isReturned) ? 'style="color: #666; background-color: #f9f9f9;"' : '';
                const repairMoney = Number(repair.repairPrice) || 0;
                
                return `
              <tr ${fadeClasses}>
                <td>${repair.ticketNumber || "-"}</td>
                <td>${repair.customerName}</td>
                <td>${repair.phoneModel}</td>
                <td>${repair.problemDescription}</td>
                <td class="amount">${repairMoney.toFixed(3)} TND</td>
                <td style="text-transform: uppercase; font-size: 0.8em;">${repair.paymentStatus}</td>
                <td style="text-transform: uppercase; font-size: 0.8em; ${(isCancelled || isReturned) ? 'color: #dc2626;' : ''}">${repair.status}</td>
                <td>${new Date(repair.createdAt).toLocaleString()}</td>
              </tr>
            `;
              })
              .join("")}
          </tbody>
        </table>
      </div>
      ` : ''}

      <div class="footer-stats">
        Total des Transactions : ${totalTransactions} | Transaction Moyenne : ${averageTransaction.toFixed(
    3
  )} TND
      </div>

      <div style="margin-top: 10px; text-align: center; color: #666; font-size: 12px;">
        Généré le ${new Date().toLocaleString()}
      </div>
    </body>
    </html>
  `;
};

export const printDailySummaryReport = (props: DailySummaryReportProps) => {
  const htmlContent = generateDailySummaryHTML(props);
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }
};
